/**
 * PasswordSection — single "Change password" button that sends a reset email.
 *
 * Drops the in-page current+new+confirm form in favor of skillful's proven
 * pattern: delegate to Firebase's reset flow so we don't have to handle
 * reauth, password policy, or error UI. Visible only when the user has a
 * password provider (gated in AccountModal).
 */

import { useState } from 'react';
import { Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { apiClient } from '@/lib/api';
import { auth } from '@/lib/firebase';
import { toast } from '@/lib/toast';

export function PasswordSection() {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    const email = auth.currentUser?.email;
    if (!email) {
      toast.error('No email on account — cannot send reset link.');
      return;
    }
    setLoading(true);
    try {
      await apiClient.requestPasswordReset(email);
      toast.success('Reset email sent. Check your inbox.');
    } catch {
      toast.error('Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-xl space-y-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">Password</h1>
        <p className="text-sm text-muted-foreground">
          We email you a reset link so you don't have to enter your current password here.
        </p>
      </header>

      <div className="rounded-md border border-border bg-card p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="text-sm text-muted-foreground">Change your account password</div>
          <Button onClick={handleClick} disabled={loading} variant="outline" className="gap-2">
            <Lock className="h-4 w-4" />
            {loading ? 'Sending…' : 'Change password'}
          </Button>
        </div>
      </div>
    </div>
  );
}
